import { inchesToMm } from '@/common/helpers/formatUtilities'

const toMm = (row, value) => (row.isImperial ? inchesToMm(value) : value)

// Flat parts: bounding box area. Tubes: bounding box volume (profile x profile x part length).
const getBoundsSizeMm = (row) => {
    const isCircle = row.profile?.toLowerCase() === 'circle'
    const dimensions = !row.profile
        ? [row.displayBoundBoxWidth ?? row.minimumBoundBoxWidth, row.displayBoundBoxHeight ?? row.minimumBoundBoxHeight]
        : isCircle
          ? [row.diameter, row.diameter, row.rotaryPartLength]
          : [row.rotaryProfileWidth, row.rotaryProfileLength, row.rotaryPartLength]

    return dimensions.every(Boolean) ? dimensions.reduce((size, value) => size * toMm(row, value), 1) : null
}

// Same names, in the same order, as the secondary processes cell shows them
const getSecondaryProcessNames = (row) => {
    const names = [
        ...(row.numberOfFolds > 0 ? ['Folding'] : []),
        ...(row.partLibraryEntrySecondaryProcesses ?? []).map((item) => item.secondaryProcess?.name),
    ]

    return names.length ? names.join(', ') : null
}

// Column id -> the values to sort by, compared in order until one differs
const PART_LIBRARY_SORT_KEYS = {
    partLibraryId: [(row) => row.partId],
    name: [(row) => row.name],
    bounds: [getBoundsSizeMm],
    cuttingTechnology: [(row) => row.cuttingTechnology?.name],
    materialThickness: [(row) => row.material?.materialName, (row) => row.thickness && toMm(row, row.thickness)],
    secondaryProcess: [getSecondaryProcessNames],
    lastQuote: [(row) => row.lastQuote?.lastModifiedDate],
    lastPrice: [(row) => row.lastQuote?.lastPrice],
    lastQuantity: [(row) => row.lastQuote?.lastQuantity],
}

// Same columns as the part order history dialog, read from the quote items the API returns
const ORDER_HISTORY_SORT_KEYS = {
    quoteName: [(row) => row.quote?.name],
    status: [(row) => row.quote?.status],
    quoteLastModified: [(row) => row.quote?.lastModifiedDateUtc],
    cuttingTechnology: [(row) => row.cuttingTechnology?.name],
    unitPrice: [(row) => row.itemPrice],
    quantity: [(row) => row.quantity],
}

const compareAscending = (first, next) =>
    typeof first === 'string'
        ? first.localeCompare(next, undefined, { numeric: true, sensitivity: 'base' })
        : first - next

const buildComparator = (sortKeys) => (order, orderBy) => {
    const direction = order === 'desc' ? -1 : 1
    const keys = sortKeys[orderBy] ?? []

    return (a, b) => {
        for (const getValue of keys) {
            const first = getValue(a) ?? null
            const next = getValue(b) ?? null

            if (first === null || next === null) {
                if (first !== next) return first === null ? 1 : -1
                continue
            }

            const result = compareAscending(first, next)
            if (result !== 0) return result * direction
        }

        return 0
    }
}

/**
 * Builds a comparator for the part library tables.
 *
 * Rows with no value for the column sort last in both directions.
 *
 * @param {'asc' | 'desc'} order - Sort direction.
 * @param {string} orderBy - Column id from the table header.
 * @returns {(a: object, b: object) => number} Comparator for `Array.prototype.sort`.
 */
export const getPartLibraryComparator = buildComparator(PART_LIBRARY_SORT_KEYS)

/**
 * Builds a comparator for the part order history dialog.
 *
 * Rows with no value for the column sort last in both directions.
 *
 * @param {'asc' | 'desc'} order - Sort direction.
 * @param {string} orderBy - Column id from the table header.
 * @returns {(a: object, b: object) => number} Comparator for `Array.prototype.sort`.
 */
export const getPartOrderHistoryComparator = buildComparator(ORDER_HISTORY_SORT_KEYS)
